// Change many products at once (Products → Select): category, brand, supplier,
// unit, tags, shelf, active, VAT. Managers only. Every field reads "Keep as it
// is" until it is tapped. The logic is in lib/bulk-edit.ts; this is the dialog.
import { Button, IconTile, Input, Modal, Segmented, Select, cx, useToast } from '@ims/ui'
import {
  AlertTriangle,
  CookingPot,
  FolderSimple,
  Hash,
  MapPin,
  Percent,
  Plus,
  Power,
  Receipt,
  Ruler,
  Tag,
  Truck,
  UtensilsCrossed,
  X,
} from '@ims/ui/icons'
import { useMemo, useState, type ComponentProps, type ReactNode } from 'react'
import type { SyncOp } from '@ims/core'
import type { ProductRow } from '@ims/data'
import { useLookups } from '../components.tsx'
import { errorMessage, useFieldGroups, useLive, useSettings } from '../hooks.ts'
import {
  countChanges,
  countWithTag,
  customerPays,
  expandFamilies,
  isEmptyChange,
  planBulkEdit,
  unitDecimalChanges,
  type BulkChange,
} from '../lib/bulk-edit.ts'
import { parseTags } from '../lib/catalog.ts'
import { money } from '../lib/format.ts'
import { findSameName } from '../lib/pick.ts'
import { insertOp, newBrand, newCategory } from '../lib/rows.ts'
import { nextSortOrder } from '../lib/categories.ts'
import { SupplierPicker } from '../party-picker.tsx'
import { useRepo, useWorkspace } from '../workspace.tsx'

/** A list's "nothing chosen yet": the field is open but changes nothing. */
const UNSET = ''
const NONE = '__none'
/** A list's last choice: make a new one here (docs/plans/add-here.md). */
const NEW = '__new'

type Key =
  | 'category'
  | 'brand'
  | 'supplier'
  | 'unit'
  | 'tags'
  | 'shelf'
  | 'active'
  | 'rate'
  | 'includes'
  | 'sold'
  | 'station'
  | 'diet'
  | 'spicy'
type Station = ProductRow['kitchen_station']

const DIETS: { value: 'veg' | 'non_veg' | 'egg' | typeof NONE; label: string }[] = [
  { value: 'veg', label: 'Veg' },
  { value: 'non_veg', label: 'Non-veg' },
  { value: 'egg', label: 'Egg' },
  { value: NONE, label: 'No mark' },
]

const STATIONS: { value: Station; label: string }[] = [
  { value: 'kitchen', label: 'Kitchen' },
  { value: 'bar', label: 'Bar' },
  { value: 'none', label: 'No ticket' },
]

/**
 * A list that ends in "+ New …": the id it stands for, and the row to save
 * first when the name is one the shop does not have. Null until it has a name.
 */
function resolve<T extends { id: string }>(
  choice: string,
  name: string,
  existing: readonly { id: string; name: string }[],
  make: (name: string) => T,
): { id: string | null | undefined; row: T | null } {
  if (choice === UNSET) return { id: undefined, row: null }
  if (choice === NONE) return { id: null, row: null }
  if (choice !== NEW) return { id: choice, row: null }
  const clean = name.trim()
  if (!clean) return { id: undefined, row: null }
  const same = findSameName(existing, clean)
  if (same) return { id: same.id, row: null }
  const row = make(clean)
  return { id: row.id, row }
}

export function BulkEditDialog({
  ids,
  onClose,
  onDone,
}: {
  ids: ReadonlySet<string>
  onClose: () => void
  onDone: () => void
}) {
  const repo = useRepo()
  const toast = useToast()
  const settings = useSettings()
  const lookups = useLookups()
  const { groups } = useFieldGroups()
  const { current } = useWorkspace()
  const tenantId = current?.tenant_id ?? ''
  const all = useLive(
    async (r) => (await r.list('products', { limit: 1_000_000 })).rows,
    [],
    ['products'],
  ).data
  const [open, setOpen] = useState<ReadonlySet<Key>>(new Set())
  const [category, setCategory] = useState(UNSET)
  const [categoryName, setCategoryName] = useState('')
  const [brand, setBrand] = useState(UNSET)
  const [brandName, setBrandName] = useState('')
  const [supplierId, setSupplierId] = useState('')
  const [unit, setUnit] = useState(UNSET)
  const [tagMode, setTagMode] = useState<'add' | 'remove'>('add')
  const [tags, setTags] = useState<string[]>([])
  const [tagText, setTagText] = useState('')
  const [shelf, setShelf] = useState('')
  const [active, setActive] = useState<'' | 'yes' | 'no'>(UNSET)
  const [rate, setRate] = useState(UNSET)
  const [includes, setIncludes] = useState<'' | 'yes' | 'no'>(UNSET)
  const [sold, setSold] = useState<'' | 'yes' | 'no'>(UNSET)
  const [station, setStation] = useState<Station | ''>(UNSET)
  const [diet, setDiet] = useState<(typeof DIETS)[number]['value'] | ''>(UNSET)
  const [spicy, setSpicy] = useState<'' | 'yes' | 'no'>(UNSET)
  const [progress, setProgress] = useState<{ done: number; total: number } | null>(null)
  const supplierName =
    useLive(
      async (r) => (supplierId ? ((await r.get('suppliers', supplierId))?.name ?? '') : ''),
      [supplierId],
      ['suppliers'],
    ).data ?? ''
  const busy = progress !== null

  const madeCategory = useMemo(
    () =>
      resolve(category, categoryName, lookups.categories, (name) =>
        newCategory({ tenantId }, { name, sort_order: nextSortOrder(lookups.categories) }),
      ),
    [category, categoryName, lookups.categories, tenantId],
  )
  const madeBrand = useMemo(
    () => resolve(brand, brandName, lookups.brands, (name) => newBrand({ tenantId }, { name })),
    [brand, brandName, lookups.brands, tenantId],
  )
  // A tag still being typed counts: nobody should have to press Enter first. It is not
  // turned into a chip when the box is left: that would move the rows under the pointer.
  const tagList = useMemo(() => parseTags([...tags, tagText].join(',')), [tags, tagText])

  const change = useMemo((): BulkChange => {
    const has = (key: Key) => open.has(key)
    return {
      ...(has('category') && madeCategory.id !== undefined ? { category_id: madeCategory.id } : {}),
      ...(has('brand') && madeBrand.id !== undefined ? { brand_id: madeBrand.id } : {}),
      ...(has('supplier') && supplierId ? { supplier_id: supplierId } : {}),
      ...(has('unit') && unit ? { unit_id: unit } : {}),
      ...(has('tags') && tagList.length
        ? { tags: tagMode === 'add' ? { add: tagList } : { remove: tagList } }
        : {}),
      ...(has('shelf') && shelf.trim() ? { shelf_location: shelf.trim() } : {}),
      ...(has('active') && active ? { is_active: active === 'yes' } : {}),
      ...(has('rate') && rate ? { tax_rate_id: rate === NONE ? null : rate } : {}),
      ...(has('includes') && includes ? { price_includes_tax: includes === 'yes' } : {}),
      ...(has('sold') && sold ? { for_sale: sold === 'yes' } : {}),
      ...(has('station') && station ? { kitchen_station: station } : {}),
      ...(has('diet') && diet ? { diet: diet === NONE ? null : diet } : {}),
      ...(has('spicy') && spicy ? { spicy: spicy === 'yes' } : {}),
    }
  }, [
    open,
    madeCategory,
    madeBrand,
    supplierId,
    unit,
    tagList,
    tagMode,
    shelf,
    active,
    rate,
    includes,
    sold,
    station,
    diet,
    spicy,
  ])
  const family = useMemo(() => (all ? expandFamilies(ids, all).rows : []), [all, ids])
  const plan = useMemo(() => (all ? planBulkEdit(ids, all, change) : null), [all, ids, change])

  const rateBp = (id: string | null) => (id ? (lookups.taxRate(id)?.rate_bp ?? 0) : 0)
  // Two products whose price to the customer changes, as a preview.
  const examples = (plan?.changed ?? [])
    .filter((p) => p.kind !== 'group' && p.sell_price_paisa > 0)
    .map((p) => {
      const before = customerPays(p.sell_price_paisa, rateBp(p.tax_rate_id), p.price_includes_tax)
      const after = customerPays(
        p.sell_price_paisa,
        rateBp(change.tax_rate_id !== undefined ? change.tax_rate_id : p.tax_rate_id),
        change.price_includes_tax ?? p.price_includes_tax,
      )
      return { p, before, after }
    })
    .filter((e) => e.before !== e.after)
    .slice(0, 2)

  const save = async () => {
    if (!plan) return
    const total = plan.changed.length
    setProgress({ done: 0, total })
    let done = 0
    // A new category or brand goes in with the first products that use it.
    const first: SyncOp[] = [
      ...(change.category_id && madeCategory.row ? [insertOp('categories', madeCategory.row)] : []),
      ...(change.brand_id && madeBrand.row ? [insertOp('brands', madeBrand.row)] : []),
    ]
    try {
      for (const [i, chunk] of plan.chunks.entries()) {
        await repo.commit(i === 0 ? [...first, ...chunk] : chunk)
        done += chunk.length
        setProgress({ done, total })
      }
      toast.success(`${total} product${total === 1 ? '' : 's'} updated`)
      onDone()
    } catch (error) {
      // Each chunk is saved on its own; saving again finishes the rest.
      toast.error(`Stopped after ${done} of ${total}`, {
        detail: `${errorMessage(error)} Saving again finishes the rest.`,
        sticky: true,
      })
      setProgress(null)
    }
  }

  const toggle = (key: Key, on: boolean) =>
    setOpen((was) => {
      const next = new Set(was)
      if (on) next.add(key)
      else next.delete(key)
      return next
    })
  const row = (
    key: Key,
    label: string,
    icon: ReactNode,
    area: ComponentProps<typeof IconTile>['area'],
    value: string,
    control: ReactNode,
  ) => (
    <BulkRow
      label={label}
      icon={icon}
      area={area}
      open={open.has(key)}
      value={value}
      disabled={busy}
      onOpen={() => toggle(key, true)}
      onKeep={() => toggle(key, false)}
    >
      {control}
    </BulkRow>
  )

  const addTag = () => {
    setTags(parseTags([...tags, tagText].join(',')))
    setTagText('')
  }
  const unitRow = lookups.unit(unit)
  const unitMoves = unit ? unitDecimalChanges(family, lookups.units, unit) : null
  const nameOf = (
    choice: string,
    made: { id: string | null | undefined; row: unknown },
    list: readonly { id: string; name: string }[],
    name: string,
    none: string,
  ) =>
    made.id === undefined
      ? ''
      : made.id === null
        ? none
        : choice === NEW && made.row
          ? `New: ${name.trim()}`
          : (list.find((x) => x.id === made.id)?.name ?? '')
  const yesNo = (v: string, yes: string, no: string) => (v === 'yes' ? yes : v === 'no' ? no : '')
  const count = countChanges(change)
  const changing = plan?.changed.length ?? 0

  return (
    <Modal
      open
      onClose={busy ? () => {} : onClose}
      title={`Change ${ids.size} product${ids.size === 1 ? '' : 's'}`}
      footer={
        <>
          <p className="mr-auto text-sm text-muted max-sm:w-full" aria-live="polite">
            {progress ? (
              `Saving ${progress.done} of ${progress.total}…`
            ) : count === 0 ? (
              'Tap what you want to change. Everything else is kept.'
            ) : changing === 0 ? (
              'All of these already match.'
            ) : (
              <>
                <b className="text-ink">
                  {count} change{count === 1 ? '' : 's'}
                </b>{' '}
                to {changing} product{changing === 1 ? '' : 's'}
                {plan && plan.unchanged > 0 ? ` · ${plan.unchanged} already match` : ''}
              </>
            )}
          </p>
          <Button variant="secondary" onClick={onClose} disabled={busy}>
            Cancel
          </Button>
          <Button
            loading={busy}
            disabled={!plan || isEmptyChange(change) || changing === 0}
            onClick={() => void save()}
          >
            {changing > 0 ? `Update ${changing} product${changing === 1 ? '' : 's'}` : 'Update'}
          </Button>
        </>
      }
    >
      <div className="space-y-4">
        <p className="text-sm text-muted">
          Only what you choose here changes. Prices and stock are not touched.
        </p>
        <BulkGroup title="Organise">
          {row(
            'category',
            'Category',
            <FolderSimple className="size-4" />,
            'stock',
            nameOf(category, madeCategory, lookups.categories, categoryName, 'No category'),
            <>
              <Select
                aria-label="Category"
                value={category}
                disabled={busy}
                onChange={(e) => setCategory(e.target.value)}
              >
                <option value={UNSET}>Choose…</option>
                <option value={NONE}>No category</option>
                {lookups.categories.map((c) => (
                  <option key={c.id} value={c.id}>
                    {c.name}
                  </option>
                ))}
                <option value={NEW}>+ New category…</option>
              </Select>
              {category === NEW && (
                <NewName
                  label="New category name"
                  value={categoryName}
                  disabled={busy}
                  onChange={setCategoryName}
                />
              )}
            </>,
          )}
          {groups.has('brand') &&
            row(
              'brand',
              'Brand',
              <Tag className="size-4" />,
              'stock',
              nameOf(brand, madeBrand, lookups.brands, brandName, 'No brand'),
              <>
                <Select
                  aria-label="Brand"
                  value={brand}
                  disabled={busy}
                  onChange={(e) => setBrand(e.target.value)}
                >
                  <option value={UNSET}>Choose…</option>
                  <option value={NONE}>No brand</option>
                  {lookups.brands.map((b) => (
                    <option key={b.id} value={b.id}>
                      {b.name}
                    </option>
                  ))}
                  <option value={NEW}>+ New brand…</option>
                </Select>
                {brand === NEW && (
                  <NewName
                    label="New brand name"
                    value={brandName}
                    disabled={busy}
                    onChange={setBrandName}
                  />
                )}
              </>,
            )}
          {row(
            'supplier',
            'Supplier',
            <Truck className="size-4" />,
            'buy',
            supplierName,
            <>
              <SupplierPicker
                label="Supplier"
                value={supplierId}
                onChange={setSupplierId}
                disabled={busy}
              />
              <Note>Who you usually buy these from. Shown on each product’s page.</Note>
            </>,
          )}
          {row(
            'unit',
            'Unit',
            <Ruler className="size-4" />,
            'stock',
            unitRow?.symbol ?? '',
            <>
              <Select
                aria-label="Unit"
                value={unit}
                disabled={busy}
                onChange={(e) => setUnit(e.target.value)}
              >
                <option value={UNSET}>Choose…</option>
                {lookups.units.map((u) => (
                  <option key={u.id} value={u.id}>
                    {u.name} ({u.symbol})
                  </option>
                ))}
              </Select>
              {unitMoves && unitMoves.toDecimal > 0 && (
                <Warn>
                  {unitMoves.toDecimal} of these {unitMoves.toDecimal === 1 ? 'is' : 'are'} sold in
                  whole numbers only today. In {unitRow?.symbol} they can be sold in parts, like
                  0.5.
                </Warn>
              )}
              {unitMoves && unitMoves.toWhole > 0 && (
                <Warn>
                  {unitMoves.toWhole} of these can be sold in parts today. In {unitRow?.symbol} only
                  whole numbers can.
                </Warn>
              )}
              <Note>Stock numbers are not converted: 10 stays 10.</Note>
            </>,
          )}
          {groups.has('shelf') &&
            row(
              'tags',
              'Tags',
              <Hash className="size-4" />,
              'stock',
              tagList.length ? `${tagMode === 'add' ? 'Add' : 'Remove'} ${tagList.length}` : '',
              <>
                <div className="flex flex-wrap items-start gap-2">
                  <Segmented
                    label="Add or remove"
                    value={tagMode}
                    onChange={setTagMode}
                    options={[
                      { value: 'add', label: 'Add' },
                      { value: 'remove', label: 'Remove' },
                    ]}
                  />
                  <div className="flex min-h-[38px] min-w-40 flex-1 flex-wrap items-center gap-1.5 rounded-xl border border-border bg-surface px-2 py-1 focus-within:border-primary focus-within:ring-2 focus-within:ring-primary/30">
                    {tags.map((t) => (
                      <span
                        key={t}
                        className="inline-flex h-6 items-center gap-1 rounded-full bg-primary-light pl-2.5 pr-1 text-xs font-semibold text-primary"
                      >
                        {t}
                        <button
                          type="button"
                          aria-label={`Take ${t} off the list`}
                          disabled={busy}
                          onClick={() => setTags(tags.filter((x) => x !== t))}
                          className="rounded-full p-0.5 hover:bg-primary/15"
                        >
                          <X className="size-3" />
                        </button>
                      </span>
                    ))}
                    <input
                      aria-label="Tags"
                      value={tagText}
                      disabled={busy}
                      placeholder={tags.length ? 'Another…' : 'Type a tag…'}
                      onChange={(e) =>
                        /[,;]/.test(e.target.value)
                          ? (setTags(parseTags([...tags, e.target.value].join(','))),
                            setTagText(''))
                          : setTagText(e.target.value)
                      }
                      onKeyDown={(e) => {
                        if (e.key === 'Enter') {
                          e.preventDefault()
                          addTag()
                        } else if (e.key === 'Backspace' && !tagText && tags.length) {
                          setTags(tags.slice(0, -1))
                        }
                      }}
                      className="min-w-20 flex-1 bg-transparent py-1 text-sm placeholder:text-subtle focus:outline-none"
                    />
                  </div>
                </div>
                <Note>
                  {tagMode === 'add'
                    ? 'Added to the tags each product already has. Nothing is replaced.'
                    : tagList.length === 1
                      ? `Taken off the ${countWithTag(family, tagList[0]!)} of these that have it. Their other tags stay.`
                      : 'Taken off the products that have them. Their other tags stay.'}
                </Note>
              </>,
            )}
          {groups.has('shelf') &&
            row(
              'shelf',
              'Shelf / rack',
              <MapPin className="size-4" />,
              'stock',
              shelf.trim(),
              <>
                <Input
                  aria-label="Shelf / rack"
                  value={shelf}
                  maxLength={60}
                  disabled={busy}
                  placeholder="A-3"
                  onChange={(e) => setShelf(e.target.value)}
                />
                <Note>Replaces the shelf written on each product.</Note>
              </>,
            )}
        </BulkGroup>
        <BulkGroup title="Status">
          {row(
            'active',
            'Active',
            <Power className="size-4" />,
            'neutral',
            yesNo(active, 'Active', 'Inactive'),
            <>
              <Segmented
                label="Active or inactive"
                className="w-fit"
                value={active}
                onChange={setActive}
                options={[
                  { value: 'yes', label: 'Active' },
                  { value: 'no', label: 'Inactive' },
                ]}
              />
              <Note>
                Inactive products leave the bill’s search and the stock alerts. Their stock, bills
                and history are kept, and you can turn them back on.
              </Note>
            </>,
          )}
        </BulkGroup>
        <BulkGroup title="VAT">
          {row(
            'rate',
            'VAT rate',
            <Percent className="size-4" />,
            'insight',
            rate === NONE ? 'No VAT' : (lookups.taxRate(rate)?.name ?? ''),
            <Select
              aria-label="VAT rate"
              value={rate}
              disabled={busy}
              onChange={(e) => setRate(e.target.value)}
            >
              <option value={UNSET}>Choose…</option>
              <option value={NONE}>No VAT</option>
              {lookups.taxRates
                .filter((t) => t.is_active)
                .map((t) => (
                  <option key={t.id} value={t.id}>
                    {t.name}
                  </option>
                ))}
            </Select>,
          )}
          {row(
            'includes',
            'Price includes VAT',
            <Receipt className="size-4" />,
            'insight',
            yesNo(includes, 'Yes', 'No'),
            <Select
              aria-label="Price includes VAT"
              value={includes}
              disabled={busy}
              onChange={(e) => setIncludes(e.target.value as typeof includes)}
            >
              <option value={UNSET}>Choose…</option>
              <option value="yes">Yes</option>
              <option value="no">No, VAT is added</option>
            </Select>,
          )}
        </BulkGroup>
        {settings?.restaurant && (
          <BulkGroup title="Restaurant">
            {row(
              'sold',
              'Sold to customers',
              <UtensilsCrossed className="size-4" />,
              'people',
              yesNo(sold, 'On the menu', 'An ingredient'),
              <Select
                aria-label="Sold to customers"
                value={sold}
                disabled={busy}
                onChange={(e) => setSold(e.target.value as typeof sold)}
              >
                <option value={UNSET}>Choose…</option>
                <option value="yes">Yes: on the menu</option>
                <option value="no">No: an ingredient</option>
              </Select>,
            )}
            {row(
              'station',
              'Orders go to',
              <CookingPot className="size-4" />,
              'people',
              STATIONS.find((s) => s.value === station)?.label ?? '',
              <>
                <Segmented
                  label="Orders go to"
                  className="w-fit"
                  value={station}
                  onChange={setStation}
                  options={STATIONS}
                />
                <Note>
                  New orders of these print on that ticket. Orders already sent stay where they are.
                </Note>
              </>,
            )}
            {row(
              'diet',
              'Mark on the guest menu',
              <UtensilsCrossed className="size-4" />,
              'people',
              DIETS.find((d) => d.value === diet)?.label ?? '',
              <Segmented
                label="Mark on the guest menu"
                className="w-fit"
                value={diet}
                onChange={setDiet}
                options={DIETS}
              />,
            )}
            {row(
              'spicy',
              'Spicy',
              <UtensilsCrossed className="size-4" />,
              'people',
              yesNo(spicy, 'Spicy', 'Not spicy'),
              <Select
                aria-label="Spicy"
                value={spicy}
                disabled={busy}
                onChange={(e) => setSpicy(e.target.value as typeof spicy)}
              >
                <option value={UNSET}>Choose…</option>
                <option value="yes">Yes: spicy</option>
                <option value="no">No: not spicy</option>
              </Select>,
            )}
          </BulkGroup>
        )}

        {plan && changing > 0 && (plan.addedVariants > 0 || examples.length > 0) && (
          <div className="space-y-2 rounded-xl bg-background p-3 text-sm" aria-live="polite">
            {plan.addedVariants > 0 && (
              <p className="text-muted">
                Includes {plan.addedVariants} more variant{plan.addedVariants === 1 ? '' : 's'}:
                variants always share these with their main product.
              </p>
            )}
            {examples.length > 0 && (
              <div className="space-y-1">
                <p className="text-muted">
                  Prices stay the same numbers, so what the customer pays changes:
                </p>
                {examples.map(({ p, before, after }) => (
                  <p key={p.id}>
                    <span className="font-medium">{p.name}</span>:{' '}
                    {money(p.sell_price_paisa, settings)} → customer pays{' '}
                    <span className="font-semibold">{money(after, settings)}</span>{' '}
                    <span className="text-muted">(was {money(before, settings)})</span>
                  </p>
                ))}
              </div>
            )}
          </div>
        )}
      </div>
    </Modal>
  )
}

function BulkGroup({ title, children }: { title: string; children: ReactNode }) {
  return (
    <section>
      <h3 className="mb-1.5 text-section text-subtle">{title}</h3>
      <div className="divide-y divide-border overflow-hidden rounded-xl border border-border bg-surface">
        {children}
      </div>
    </section>
  )
}

/**
 * One field of the dialog: a button reading "Keep as it is" until it is tapped,
 * then its control, with an X that puts it back. Its controls are named by the
 * field; nothing else in the row may carry that name (tests find them by it).
 */
function BulkRow({
  label,
  icon,
  area,
  open,
  value,
  disabled,
  onOpen,
  onKeep,
  children,
}: {
  label: string
  icon: ReactNode
  area: ComponentProps<typeof IconTile>['area']
  open: boolean
  /** What the field will be set to, in a word; empty while nothing is chosen. */
  value: string
  disabled: boolean
  onOpen: () => void
  onKeep: () => void
  children: ReactNode
}) {
  const tile = (
    <IconTile area={area} size="sm">
      {icon}
    </IconTile>
  )
  if (!open)
    return (
      <button
        type="button"
        disabled={disabled}
        onClick={onOpen}
        className="flex min-h-12 w-full items-center gap-3 px-3.5 text-left text-sm hover:bg-soft"
      >
        {tile}
        <span className="flex-1 font-semibold">{label}</span>
        <span className="text-subtle">Keep as it is</span>
        <Plus className="size-4 text-subtle" aria-hidden />
      </button>
    )
  return (
    <div className={cx('px-3.5 pb-3.5 pt-2.5', 'bg-primary-light/40')}>
      <div className="flex min-h-7 items-center gap-3 text-sm">
        {tile}
        <span className="flex-1 font-bold">{label}</span>
        <span className="truncate font-semibold text-primary" translate="no">
          {value}
        </span>
        <button
          type="button"
          aria-label="Keep as it is"
          title="Keep as it is"
          disabled={disabled}
          onClick={onKeep}
          className="rounded-lg p-1 text-muted hover:bg-soft hover:text-ink"
        >
          <X className="size-4" />
        </button>
      </div>
      <div className="mt-2 sm:ml-10">{children}</div>
    </div>
  )
}

function NewName({
  label,
  value,
  disabled,
  onChange,
}: {
  label: string
  value: string
  disabled: boolean
  onChange: (name: string) => void
}) {
  return (
    <label className="mt-2 block">
      <span className="mb-1 block text-sm font-medium text-muted">{label}</span>
      <Input
        autoFocus
        maxLength={80}
        value={value}
        disabled={disabled}
        onChange={(e) => onChange(e.target.value)}
      />
    </label>
  )
}

function Note({ children }: { children: ReactNode }) {
  return <p className="mt-1.5 text-xs text-muted">{children}</p>
}

function Warn({ children }: { children: ReactNode }) {
  return (
    <p className="mt-2 flex items-start gap-2 rounded-xl bg-warning-light px-3 py-2 text-xs text-warning">
      <AlertTriangle className="mt-px size-4 shrink-0" aria-hidden />
      <span>{children}</span>
    </p>
  )
}
